import { useRef, type ReactNode } from 'react';
import { Button } from './button';
import { Dialog } from './dialog';

/**
 * A small accessible confirmation dialog (role="alertdialog") for consequential
 * one-click actions — e.g. a legal attestation or an irreversible delete. Built
 * on the shared Dialog: states what the person is about to do in `children`,
 * focuses Cancel (the safe choice) on open, traps focus, returns it to the
 * opener on close, closes on Escape (topmost only) or the scrim unless pending,
 * and runs `onConfirm` only from the explicit confirm button.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  pending = false,
  danger = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  pending?: boolean;
  /** An irreversible destructive confirm — the confirm button is red. */
  danger?: boolean;
  /** Hold the confirm button until the person has done something first (e.g.
   * ticked an attestation). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      role="alertdialog"
      size="sm"
      title={title}
      onClose={onCancel}
      busy={pending}
      initialFocusRef={cancelRef}
      footer={
        <div className="ml-auto flex gap-2">
          <Button ref={cancelRef} variant="outline" disabled={pending} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'destructive' : 'default'}
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
          >
            {pending ? 'Saving…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </Dialog>
  );
}
